import React, { forwardRef, useState } from "react";

interface MemberRemovalModalProps {
  memberName: string;
  labName: string;
  reason: string;
  onReasonChange: (value: string) => void;
  notifyUser: boolean;
  onNotifyUserChange: (checked: boolean) => void;
  handleRemoveMember: () => Promise<void>;
  handleCancel?: () => void;
}

export interface MemberRemovalModalRef {
  showModal: () => void;
  close: () => void;
}

export const MemberRemovalModal = forwardRef<MemberRemovalModalRef, MemberRemovalModalProps>(({
  memberName,
  labName,
  reason,
  onReasonChange,
  notifyUser,
  onNotifyUserChange,
  handleRemoveMember,
  handleCancel
}, ref) => {
  const [loading, setLoading] = useState(false);
  const dialogRef = React.useRef<HTMLDialogElement>(null);

  React.useImperativeHandle(ref, () => ({
    showModal: () => dialogRef.current?.showModal(),
    close: () => dialogRef.current?.close()
  }));

  return (
    <dialog ref={dialogRef} className="modal fixed inset-0 flex items-center justify-center">
      <div className="modal-box">
        <h3 className="font-bold text-lg">Remove {memberName} from {labName}?</h3>
        <p className="py-4">
          They will lose access to this lab and its assessments.
        </p>
        <form method="dialog" className="space-y-4">
          <div className="form-control">
            <label className="label" htmlFor="removal-reason">
              <span className="label-text">Reason (optional)</span>
            </label>
            <textarea
              id="removal-reason"
              className="textarea textarea-bordered focus:outline-none focus:ring-0 focus:border-2 focus:border-primary"
              placeholder="Provide context for this removal"
              value={reason}
              onChange={(e) => onReasonChange(e.target.value)}
              rows={3}
            />
          </div>

          <div className="form-control">
            <label className="cursor-pointer label justify-start gap-3">
              <input
                type="checkbox"
                className="checkbox"
                checked={notifyUser}
                onChange={(e) => onNotifyUserChange(e.target.checked)}
              />
              <span className="label-text">Email the member about this change</span>
            </label>
          </div>

          <div className="modal-action">
            <button
              className="btn btn-outline"
              disabled={loading}
              onClick={(e) => {
                e.preventDefault();
                if (handleCancel) handleCancel();
                dialogRef.current?.close();
              }}
            >
              Cancel
            </button>
            <button
              className="btn btn-error"
              onClick={async (e) => {
                e.preventDefault()
                setLoading(true);
                try {
                  await handleRemoveMember();
                  dialogRef.current?.close();
                } finally {
                  setLoading(false);
                }
              }}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  Removing...
                </>
              ) : (
                'Yes, remove'
              )}
            </button>
          </div>
        </form>
      </div>
    </dialog>
  );
});

MemberRemovalModal.displayName = 'MemberRemovalModal';
